import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';

import GridLayout from '../../components/layout/GridLayout';
import Menu from '../../components/layout/Menu';
import Card from '../../components/ui/Card';

type PlanType = 'ENTRY' | 'CORE' | 'REHABILITATION';

export default function AboutPage({
  isMenuOpen = false
}: {
  isMenuOpen?: boolean;
}) {
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  const stripePrices: Record<PlanType, string> = {
    ENTRY: 'price_1TVnCkJdEQKAUUxnAWOL6uPt',
    CORE: 'price_1TVnLjJdEQKAUUxnS7MMBMg3',
    REHABILITATION: 'price_1TVnT6JdEQKAUUxntGQiegtl'
  };

  const planDetails: Record<
    PlanType,
    {
      desc: string;
      duration: string;
      price: string;
    }
  > = {
    ENTRY: {
      desc: 'Classic Pilates - One week trial for beginners. 1 online sessions x 30min',
      duration: '1 Week',
      price: '$10'
    },
    CORE: {
      desc: 'Consistent Pilates training with guided structure. 3 times online sessions x 30min ' ,
      duration: '2 Months',
      price: '$19'
    },
    REHABILITATION: {
      desc: 'Posture correction and recovery focused training. 2 times online sessions x 30min',
      duration: '1 Month',
      price: '$15'
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;

    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || ''
          },
          body: JSON.stringify({
            priceId: stripePrices[selectedPlan]
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Checkout failed');
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GridLayout
      isMenuOpen={isMenuOpen}
      left={<Menu />}
      right={<Box />}
      main={
        <Stack spacing={4}>

          {/* Intro Card */}
          <Card
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => setClicked(!clicked)}
            sx={{
              cursor: 'pointer',
              p: 3,
              transition: '0.3s',
              bgcolor: hovered || clicked ? '#f0fff9' : 'white',
              border: '1px solid',
              borderColor:
                hovered || clicked ? '#34de9aab' : '#eee'
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: '#34de9aab',
                mb: 2
              }}
            >
              Fully Personalized Programs
            </Typography>

            <Typography
              sx={{
                whiteSpace: 'pre-line',
                lineHeight: 1.8
              }}
            >
              {hovered || clicked
                ? 'Tailored to your body condition.'
                : 'あなたの身体に合わせたオンラインピラティス。'}
            </Typography>
          </Card>

          {/* Plans */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 'bold',
                color: '#34de9aab'
              }}
            >
              PLANS
            </Typography>

            <Stack spacing={2}>
              {(Object.keys(planDetails) as PlanType[]).map(
                (name) => (
                  <Box
                    key={name}
                    onClick={() => setSelectedPlan(name)}
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderRadius: 2,
                      cursor: 'pointer',
                      borderColor:
                        selectedPlan === name
                          ? '#34de9aab'
                          : '#eee',
                      bgcolor:
                        selectedPlan === name
                          ? '#f0fff9'
                          : 'white'
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {name}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#34de9aab',
                        mt: 1
                      }}
                    >
                      {planDetails[name].price} /{' '}
                      {planDetails[name].duration}
                    </Typography>

                    {selectedPlan === name && (
                      <Typography sx={{ mt: 2 }}>
                        {planDetails[name].desc}
                      </Typography>
                    )}
                  </Box>
                )
              )}
            </Stack>
          </Box>

          {/* Checkout */}
          <Box>
            <Card
              sx={{
                border: '2px dashed #34de9aab',
                textAlign: 'center',
                p: 3
              }}
            >
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {selectedPlan ? (
                <Button
                  variant="contained"
                  onClick={handleCheckout}
                  disabled={loading}
                  sx={{
                    bgcolor: '#34de9aab',
                    '&:hover': {
                      bgcolor: '#2bc48a'
                    },
                    px: 4
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={22}
                      sx={{ color: 'white' }}
                    />
                  ) : (
                    'PAY WITH CARD'
                  )}
                </Button>
              ) : (
                <Typography color="text.secondary">
                  Select a plan first
                </Typography>
              )}
            </Card>
          </Box>

        </Stack>
      }
    />
  );
}